// src/pages/admin/HeroBanners.jsx
import { useEffect, useState } from 'react'
import { db, storage } from '../../firebase'
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { useUiFeedback } from '../../context/UiFeedbackContext'

const EMPTY_FORM = {
  title: '',
  subtitle: '',
  imageUrl: '',          // URL finale de l'image dans Storage
  href: '/catalog',
  order: 1,
  active: true,
  backgroundColor: '#006d38',
}

// ✅ Ratio conseillé pour le hero (16:9 ou ~3:1)
const MIN_RATIO = 1.6   // ~16:10
const MAX_RATIO = 3.5   // ~3:1

export default function HeroBannersAdmin() {
  const [items, setItems] = useState([])
  const [form, setForm] = useState(EMPTY_FORM)
  const [editingId, setEditingId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [imageFile, setImageFile] = useState(null)   // fichier local
  const [uploading, setUploading] = useState(false)  // état upload

  const { notify } = useUiFeedback?.() || { notify: null }

  // 🔁 Subscribe temps réel à la collection homeBanners
  useEffect(() => {
    const q = query(
      collection(db, 'homeBanners'),
      orderBy('order', 'asc')
    )

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
        setItems(rows)
      },
      (err) => {
        console.error(err)
        setError("Erreur lors du chargement des sliders.")
      }
    )

    return () => unsub()
  }, [])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setForm((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : name === 'order'
          ? Number(value) || 1
          : value,
    }))
  }

  // 🔹 Changement de fichier image + contrôle de ratio
  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null
    if (!file) {
      setImageFile(null)
      return
    }

    // URL temporaire pour lire l'image
    const objectUrl = URL.createObjectURL(file)
    const img = new Image()

    img.onload = () => {
      const { width, height } = img
      URL.revokeObjectURL(objectUrl)

      if (!width || !height) {
        setError("Impossible de lire les dimensions de l'image.")
        setImageFile(null)
        e.target.value = ''
        return
      }

      const ratio = width / height

      if (ratio < MIN_RATIO || ratio > MAX_RATIO) {
        // ❌ Ratio hors plage
        setError(
          `Format non optimal : ${Math.round(ratio * 100) / 100}:1 (${width}x${height}). ` +
          'Utilise une image horizontale 16:9 ou 3:1, par ex. 1600x900 ou 1500x500 px.'
        )
        setImageFile(null)
        e.target.value = ''
        return
      }

      // ✅ Ratio OK
      setError(null)
      setImageFile(file)

      // On vide l’ancienne URL manuelle si on avait édité
      setForm((prev) => ({
        ...prev,
        imageUrl: prev.imageUrl || '',
      }))
    }

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      setError("Impossible de charger cette image. Vérifie le fichier.")
      setImageFile(null)
      e.target.value = ''
    }

    img.src = objectUrl
  }

  const resetForm = () => {
    setForm(EMPTY_FORM)
    setEditingId(null)
    setImageFile(null)
    setUploading(false)
    setError(null)
  }

  const handleEdit = (item) => {
    setEditingId(item.id)
    setForm({
      title: item.title || '',
      subtitle: item.subtitle || '',
      imageUrl: item.imageUrl || '',
      href: item.href || '/catalog',
      order: item.order ?? 1,
      active: Boolean(item.active),
      backgroundColor: item.backgroundColor || '#006d38',
    })
    setImageFile(null)
    setError(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer le slider "${item.title || item.id}" ?`)) {
      return
    }
    try {
      await deleteDoc(doc(db, 'homeBanners', item.id))
      if (notify) notify('Slider supprimé.')
    } catch (e) {
      console.error(e)
      setError("Erreur lors de la suppression.")
    }
  }

  const toggleActive = async (item) => {
    try {
      await updateDoc(doc(db, 'homeBanners', item.id), {
        active: !item.active,
      })
      if (notify) notify('Statut mis à jour.')
    } catch (e) {
      console.error(e)
      setError("Erreur lors de la mise à jour du statut.")
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (!form.title) {
      setError('Le titre est obligatoire.')
      return
    }

    if (!form.imageUrl && !imageFile) {
      setError("L'image du slider est obligatoire.")
      return
    }

    setLoading(true)

    try {
      let imageUrl = form.imageUrl

      // 🔹 Si un nouveau fichier est choisi → upload vers Storage
      if (imageFile) {
        setUploading(true)
        const path = `homeBanners/${Date.now()}_${imageFile.name}`
        const storageRef = ref(storage, path)
        await uploadBytes(storageRef, imageFile)
        imageUrl = await getDownloadURL(storageRef)
        setUploading(false)
      }

      const payload = {
        title: form.title,
        subtitle: form.subtitle,
        imageUrl: imageUrl,                     // URL finale
        href: form.href || '/catalog',
        order: Number(form.order) || 1,
        active: !!form.active,
        backgroundColor: form.backgroundColor || '#006d38',
      }

      if (editingId) {
        await updateDoc(doc(db, 'homeBanners', editingId), {
          ...payload,
          updatedAt: serverTimestamp(),
        })
        if (notify) notify('Slider mis à jour.')
      } else {
        await addDoc(collection(db, 'homeBanners'), {
          ...payload,
          createdAt: serverTimestamp(),
        })
        if (notify) notify('Nouveau slider créé.')
      }

      resetForm()
    } catch (e) {
      console.error(e)
      setError("Erreur lors de l'enregistrement du slider.")
      setUploading(false)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="container py-3">
      <h3 className="mb-3">Admin – Sliders Hero (homeBanners)</h3>

      {/* Formulaire création / édition */}
      <div className="card-modern p-3 mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            {editingId ? 'Modifier un slider hero' : 'Ajouter un slider hero'}
          </h5>
          {editingId && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={resetForm}
            >
              Annuler la modification
            </button>
          )}
        </div>

        {error && (
          <div className="alert alert-danger py-2">{error}</div>
        )}

        <form className="row g-3" onSubmit={handleSubmit}>
          <div className="col-md-6">
            <label className="form-label">Titre principal</label>
            <input
              className="form-control"
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="Ex : Achetez sans compte, simplement."
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Sous-titre</label>
            <input
              className="form-control"
              name="subtitle"
              value={form.subtitle}
              onChange={handleChange}
              placeholder="Ex : Paiement Mobile Money ou à la livraison..."
            />
          </div>

          <div className="col-md-8">
            <label className="form-label">Image du slider</label>
            <input
              type="file"
              accept="image/*"
              className="form-control"
              onChange={handleFileChange}
            />
            <div className="form-text">
              Utilise une image horizontale (format 16:9 ou 3:1), par ex. 1600×900 ou 1500×500 px.
            </div>

            {/* Aperçu de l'image actuelle */}
            {form.imageUrl && (
              <div className="mt-2">
                <div
                  style={{
                    width: '100%',
                    maxWidth: 320,
                    height: 120,
                    borderRadius: 12,
                    overflow: 'hidden',
                    background: form.backgroundColor || '#0b1226',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img
                    src={form.imageUrl}
                    alt={form.title || 'Slider'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              </div>
            )}

            {uploading && (
              <div className="small text-muted mt-1">
                Upload de l&apos;image en cours…
              </div>
            )}
          </div>

          <div className="col-md-4">
            <label className="form-label">Lien (clic sur le slide)</label>
            <input
              className="form-control"
              name="href"
              value={form.href}
              onChange={handleChange}
              placeholder="/catalog ou https://..."
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">Ordre d&apos;affichage</label>
            <input
              type="number"
              className="form-control"
              name="order"
              value={form.order}
              onChange={handleChange}
              min={1}
            />
            <div className="form-text">
              Plus le nombre est petit, plus le slide est montré en premier.
            </div>
          </div>

          <div className="col-md-3">
            <label className="form-label">Couleur de fond</label>
            <input
              type="color"
              className="form-control form-control-color"
              name="backgroundColor"
              value={form.backgroundColor}
              onChange={handleChange}
            />
            <div className="form-text">
              Utilisée si l&apos;image a des bords transparents (optionnel).
            </div>
          </div>

          <div className="col-md-3 d-flex align-items-center">
            <div className="form-check mt-4">
              <input
                className="form-check-input"
                type="checkbox"
                id="activeCheck"
                name="active"
                checked={form.active}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="activeCheck">
                Actif (visible sur le site)
              </label>
            </div>
          </div>

          <div className="col-12">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || uploading}
            >
              {loading || uploading
                ? 'Enregistrement...'
                : editingId
                ? 'Mettre à jour'
                : 'Créer le slider'}
            </button>
          </div>
        </form>
      </div>

      {/* Liste des sliders existants */}
      <div className="card-modern p-3">
        <h5 className="mb-3">Sliders existants</h5>

        {items.length === 0 && (
          <div className="alert alert-light border small mb-0">
            Aucun slider pour le moment. Ajoute-en un avec le formulaire
            ci-dessus.
          </div>
        )}

        {items.map((item) => (
          <div
            key={item.id}
            className="d-flex align-items-center gap-3 border rounded p-2 mb-2"
          >
            <div
              style={{
                width: 120,
                height: 60,
                borderRadius: 8,
                overflow: 'hidden',
                background: item.backgroundColor || '#0b1226',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt={item.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <span className="small text-white">Pas d&apos;image</span>
              )}
            </div>

            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-dark">{item.order ?? 1}</span>
                <strong>{item.title || '(Sans titre)'}</strong>
                {!item.active && (
                  <span className="badge bg-secondary">Inactif</span>
                )}
              </div>
              {item.subtitle && (
                <div className="small text-muted">{item.subtitle}</div>
              )}
              {item.href && (
                <div className="small">
                  <span className="text-muted">Lien : </span>
                  <code>{item.href}</code>
                </div>
              )}
            </div>

            <div className="d-flex flex-column align-items-end gap-1">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => handleEdit(item)}
              >
                Modifier
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => toggleActive(item)}
              >
                {item.active ? 'Désactiver' : 'Activer'}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => handleDelete(item)}
              >
                Supprimer
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
